import { AutoAwesome, CloudUpload, CheckCircle, Description } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Collapse,
    MenuItem,
    Snackbar,
    TextField,
    Typography,
    keyframes,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import api from 'api';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { RootState } from 'redux/store';
import type { CountryCode } from 'types/countries';
import { countryLabelKey, cvUploadCountryCode } from 'utils/countries';
import { emitInitialDocumentsRefresh } from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/CLIENTVIEW/components/ClientProfile/components/InitialDocuments/initialDocumentsEvents';

// ── Branded keyframe animations ──
const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.08); opacity: 0.85; }
`;

const sparkle = keyframes`
  0%, 100% { transform: rotate(0deg) scale(1); }
  25% { transform: rotate(-10deg) scale(1.2); }
  75% { transform: rotate(10deg) scale(1.15); }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

type AlertType = 'success' | 'error' | 'info';

const UploadCVButton = () => {
    const { language_setting: language, id: userId } = useSelector(
        (state: RootState) => state.user.entity
    );
    const selectedUserId = useSelector((state: RootState) => state.selectedAccount.selectedUserId);
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { codes } = useEnabledCountries();
    const [selectedCountry, setSelectedCountry] = useState<CountryCode | null>(null);
    const [countryError, setCountryError] = useState(false);
    const countryCode = cvUploadCountryCode(codes, selectedCountry);
    const needsCountryChoice = codes.length > 1;

    const [loading, setLoading] = useState(false);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [fileName, setFileName] = useState('');
    const [snackbar, setSnackbar] = useState<{ open: boolean; type: AlertType; message: string }>({
        open: false,
        type: 'info',
        message: '',
    });

    const handleButtonClick = useCallback(() => {
        if (!countryCode) {
            setCountryError(needsCountryChoice);
            return;
        }
        fileInputRef.current?.click();
    }, [countryCode, needsCountryChoice]);

    const handleFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            if (!e.target.files || e.target.files.length === 0) return;

            const file = e.target.files[0];
            if (!countryCode) return;
            setFileName(file.name);
            setLoading(true);
            setUploadComplete(false);

            try {
                await api.tableInput.postCV(file, null, countryCode);
                emitInitialDocumentsRefresh({ userId: selectedUserId || userId });
                setUploadComplete(true);
                setSnackbar({
                    open: true,
                    type: 'success',
                    message: t('CVSuccess', { lng: language }),
                });

                // Auto-hide the success state after 4 seconds
                setTimeout(() => {
                    setUploadComplete(false);
                    setFileName('');
                }, 4000);
            } catch {
                setSnackbar({
                    open: true,
                    type: 'error',
                    message: t('somethingWentWrong', { lng: language }),
                });
                setFileName('');
            } finally {
                setLoading(false);
                e.target.value = '';
            }
        },
        [countryCode, language, selectedUserId, t, userId]
    );

    return (
        <>
            <Box
                sx={{
                    animation: `${fadeSlideIn} 0.4s ease-out`,
                }}
            >
                {/* ── Main Upload Card ── */}
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: loading
                            ? 'transparent'
                            : uploadComplete
                              ? 'success.light'
                              : 'divider',
                        background: loading
                            ? 'linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.06) 100%)'
                            : uploadComplete
                              ? 'linear-gradient(135deg, rgba(46,125,50,0.04) 0%, rgba(76,175,80,0.04) 100%)'
                              : 'background.paper',
                        transition: 'all 0.4s ease',
                        ...(loading && {
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                borderRadius: 'inherit',
                                padding: '1.5px',
                                background:
                                    'linear-gradient(90deg, #667eea, #764ba2, #667eea, #764ba2)',
                                backgroundSize: '200% 100%',
                                animation: `${shimmer} 2s linear infinite`,
                                WebkitMask:
                                    'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                WebkitMaskComposite: 'xor',
                                maskComposite: 'exclude',
                            },
                        }),
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 1.5, sm: 2 },
                            px: 2.5,
                            py: 2,
                        }}
                    >
                        {/* Icon + Text row */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flex: 1,
                                minWidth: 0,
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            {/* Icon area */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    background: loading
                                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                        : uploadComplete
                                          ? 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)'
                                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    animation: loading
                                        ? `${pulse} 1.5s ease-in-out infinite`
                                        : 'none',
                                    transition: 'background 0.4s ease',
                                }}
                            >
                                {loading ? (
                                    <AutoAwesome
                                        sx={{
                                            color: '#fff',
                                            fontSize: 22,
                                            animation: `${sparkle} 1s ease-in-out infinite`,
                                        }}
                                    />
                                ) : uploadComplete ? (
                                    <CheckCircle sx={{ color: '#fff', fontSize: 22 }} />
                                ) : (
                                    <AutoAwesome sx={{ color: '#fff', fontSize: 22 }} />
                                )}
                            </Box>

                            {/* Text content */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 600,
                                        color: uploadComplete ? 'success.dark' : 'text.primary',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {loading
                                        ? 'Analyzing your CV...'
                                        : uploadComplete
                                          ? 'Form auto-filled successfully!'
                                          : t('autoFillForm', { lng: language })}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                        mt: 0.25,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {loading
                                        ? 'AI is extracting information from your resume'
                                        : uploadComplete
                                          ? 'Your form fields have been populated with CV data'
                                          : t('uploadCVText', { lng: language })}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Action button */}
                        {!loading && !uploadComplete && codes.length > 1 && (
                            <TextField
                                select
                                required
                                size="small"
                                label={t('selectCountry', { lng: language })}
                                value={selectedCountry ?? ''}
                                error={countryError}
                                helperText={t('cvDestinationHelper', { lng: language })}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setCountryError(false);
                                    setSelectedCountry(
                                        codes.find((code) => code === value) ?? null
                                    );
                                }}
                                sx={{ minWidth: { xs: '100%', sm: 180 } }}
                            >
                                {codes.map((code) => (
                                    <MenuItem key={code} value={code}>
                                        {t(countryLabelKey(code), { lng: language })}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        {!loading && !uploadComplete && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<CloudUpload sx={{ fontSize: '18px !important' }} />}
                                onClick={handleButtonClick}
                                disableElevation
                                sx={{
                                    flexShrink: 0,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 0.8,
                                    fontWeight: 600,
                                    fontSize: '0.8125rem',
                                    width: { xs: '100%', sm: 'auto' },
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    '&:hover': {
                                        background:
                                            'linear-gradient(135deg, #5a6fd6 0%, #6a4196 100%)',
                                    },
                                }}
                            >
                                Upload CV
                            </Button>
                        )}
                    </Box>

                    {/* ── Loading Progress Bar ── */}
                    <Collapse in={loading}>
                        <Box sx={{ px: 2.5, pb: 2 }}>
                            <Box
                                sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    bgcolor: 'rgba(102,126,234,0.12)',
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        borderRadius: 2,
                                        background:
                                            'linear-gradient(90deg, #667eea, #764ba2, #667eea)',
                                        backgroundSize: '200% 100%',
                                        animation: `${shimmer} 1.5s ease-in-out infinite`,
                                    }}
                                />
                            </Box>
                        </Box>
                    </Collapse>

                    {/* ── File name indicator during upload ── */}
                    <Collapse in={loading && !!fileName}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2.5,
                                pb: 2,
                            }}
                        >
                            <Description sx={{ fontSize: 16, color: 'text.disabled' }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {fileName}
                            </Typography>
                        </Box>
                    </Collapse>
                </Box>
            </Box>

            {/* Hidden file input */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx"
            />

            {/* Snackbar notification */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.type}
                    variant="filled"
                    sx={{ width: '100%', borderRadius: 2 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default UploadCVButton;
